/// <reference lib="dom" />
import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import App from './App.tsx';
import './index.css';
import { AuthProvider } from './lib/auth.tsx';
import { WalletProvider } from './lib/walletContext.tsx';
import { SettingsProvider } from './lib/settings/settingsContext.tsx';
import { Toaster } from 'react-hot-toast';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { LogViewer } from './components/LogViewer.tsx';
import { logger, AnalyticsEvent } from './services/logger.ts';
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/], // Adjust to your backend
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, sample the session when an error occurs.
  sendDefaultPii: true,
});

// Initialize analytics
const initializeAnalytics = () => {
  // Track initial page view
  logger.trackEvent(AnalyticsEvent.PAGE_VIEW, {
    path: window.location.pathname,
    referrer: document.referrer,
  });

  // Track performance metrics
  if ('performance' in window) {
    window.addEventListener('load', () => {
      const perfData = (window.performance as Performance & { timing: PerformanceTiming }).timing;
      const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
      
      logger.trackEvent(AnalyticsEvent.PERFORMANCE, {
        pageLoadTime,
        domContentLoaded: perfData.domContentLoadedEventEnd - perfData.navigationStart,
        firstPaint: perfData.responseEnd - perfData.navigationStart,
      });
    });
  }
};

// Initialize analytics
initializeAnalytics();

// The Sentry ErrorBoundary should wrap your application if you want it to catch React rendering errors.
// Your custom ErrorBoundary can still be used, and it will call logger.error, which we will update to report to Sentry.
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
        <ErrorBoundary>
          <AuthProvider>
            <SettingsProvider>
              <WalletProvider>
                <App />
                <Toaster position="top-right" />
                {import.meta.env.DEV && <LogViewer />}
              </WalletProvider>
            </SettingsProvider>
          </AuthProvider>
        </ErrorBoundary>
      </Sentry.ErrorBoundary>
    </HelmetProvider>
  </React.StrictMode>
);